import React, { useState, useEffect } from 'react';
import { Spin, Empty, Card, Row, Col, Typography } from 'antd';
import CustomList from '../../components/List';
import ListItem from '../../components/ListItem';
import CustomPagination from '../../components/CustomPagination';
import API from '../../../API';

const { Text, Title } = Typography;

const ProjectFinanciersTab = ({ projectId }) => {
    const [financiers, setFinanciers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFinancierId, setSelectedFinancierId] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
    });
    const [currentPage, setCurrentPage] = useState(1);

    const fetchFinanciers = async (page = 1, pageSize = pagination.per_page) => {
        if (!projectId) return;
        
        setLoading(true);
        try {
            const response = await API.getProjectFinanciers(projectId, page, pageSize);
            
            let financiersData = [];
            let paginationData = pagination;
            
            if (response && response?.data?.data) {
                financiersData = response.data.data;
            } else if (Array.isArray(response?.data)) {
                financiersData = response?.data;
            }
            
            if (response && response?.data?.pagination) {
                paginationData = response?.data?.pagination;
            }
            
            setFinanciers(financiersData);
            setPagination(paginationData);
        } catch (error) {
            console.error('Error fetching financiers:', error);
            setFinanciers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFinanciers();
    }, [projectId]);

    const handleFinanciersPageChange = (page, pageSize) => {
        setCurrentPage(page);
        fetchFinanciers(page, pageSize);
    };


    if (loading && financiers.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!loading && financiers.length === 0) {
        return (
            <Empty 
                description="No financiers found for this project"
                style={{ margin: '50px 0' }}
            />
        );
    }

    return (
        <Card>
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={24}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Title level={4} style={{ margin: 0 }}>Project Financiers</Title>
                    </div>
                    <div>
                        {loading ? (
                            <Spin />
                        ) : (
                            <>
                                <CustomList
                                    itemName="Financiers"
                                    items={financiers}
                                    page={currentPage}
                                    itemCount={pagination?.total || (financiers?.length || 0)}
                                    loading={loading}
                                    headerLayout={[
                                        { xxl: 12, xl: 12, lg: 12, md: 12, sm: 24, xs: 24, header: 'Name' },
                                        { xxl: 8, xl: 8, lg: 8, md: 8, sm: 24, xs: 24, header: 'Amount' },
                                    ]}
                                    renderListItem={({ item }) => (
                                        <ListItem
                                            key={item.id}
                                            name={item.name}
                                            item={item}
                                            isPackage={false}
                                            className={selectedFinancierId === item.id ? 'selected' : ''}
                                            onClick={() => setSelectedFinancierId(item.id)}
                                        >
                                            <Row gutter={[8, 0]} style={{ width: '100%' }}>
                                                <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24} className="contentEllipse">
                                                    <div className="mobile-label">Name</div>
                                                    <Text strong>{item.name || '-'}</Text>
                                                </Col>
                                                <Col xxl={8} xl={8} lg={8} md={8} sm={24} xs={24} className="contentEllipse">
                                                    <div className="mobile-label">Amount</div>
                                                    <Text>
                                                        {item.amount && item.currency ? (
                                                            <>
                                                                {new Intl.NumberFormat('en-US', {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2
                                                                }).format(item.amount)} {item.currency.iso}
                                                            </>
                                                        ) : '-'}
                                                    </Text>
                                                </Col>
                                            </Row>
                                        </ListItem>
                                    )}
                                    showToolbar={false}
                                />
                                
                                {pagination && pagination.total > 0 && (
                                    <CustomPagination 
                                        pagination={pagination}
                                        onPageChange={handleFinanciersPageChange}
                                        loading={loading}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </Col>
            </Row>
        </Card>
    );
};

export default ProjectFinanciersTab;
